import LocalOffer from "@material-design-icons/svg/outlined/local_offer.svg?react";
import Person from "@material-design-icons/svg/outlined/person_outline.svg?react";
import LocationIcon from "@material-design-icons/svg/outlined/location_on.svg?react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  EmailShareButton,
  FacebookShareButton,
  WhatsappShareButton,
} from "react-share";
import { locationPriceLevelValues } from "~/db/schema";
import { toast } from "sonner";
import noImageFound from "~/assets/no-image.svg";
import { IMAGE_SIZES, buildImageUrl } from "~/lib/utils";
import { getMaxCapacity } from "~/lib/utils/location-capacity";
export interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: {
    name: string;
    images: Array<{
      id?: number | null;
      path?: string | null;
      alt?: string | null;
    }>;
    locationTypes: Array<{
      id: number;
      name: string;
    }>;
    descriptionForm?: {
      german?: string | null;
      english?: string | null;
    } | null;
    description?: string | null;
    priceLevel?: string | null;
    capacityStanding?: number | null;
    capacitySeated?: number | null;
    seatingBanquet?: number | null;
    seatingBlock?: number | null;
    seatingParliamentary?: number | null;
    seatingRow?: number | null;
    seatingTable?: number | null;
    seatingUShape?: number | null;
    seatingStanding?: number | null;
    city?: string | null;
  };
  shareUrl: string;
}

export function ShareDialog({
  open,
  onOpenChange,
  location,
  shareUrl,
}: ShareDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeButtonVariant="white"
        className="w-[720px] max-h-[90vh] p-0 gap-0 flex flex-col data-[state=open]:slide-in-from-bottom-4"
      >
        <DialogHeader
          variant="black"
          className="flex flex-row items-center justify-between px-6 py-3 space-y-0 shrink-0"
        >
          <DialogTitle>Teilen</DialogTitle>
          <DialogDescription className="sr-only">
            Wähle eine Plattform aus, um diese Location zu teilen.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col md:flex-row gap-4 p-8 items-start">
          <div className="relative shrink-0">
            <img
              src={
                location.images[0]?.path
                  ? buildImageUrl(location.images[0]!.path!, {
                      size: IMAGE_SIZES.cardThumb,
                    })
                  : noImageFound
              }
              alt={location.images[0]?.alt || ""}
              className="rounded-md w-[180px] h-[170px] object-cover"
            />
            {location.images.length > 0 ? (
              <span className="absolute bottom-1.5 right-1.5 text-xs bg-white/80 rounded-md px-1.5 py-1">
                1 / {location.images.length}
              </span>
            ) : null}
          </div>
          <div>
            <h2 className="font-bold text-2xl mb-2">{location.name}</h2>
            <dl>
              <dt className="text-base font-medium">
                {location.locationTypes.length
                  ? location.locationTypes[0].name
                  : null}
              </dt>
              <dd className="text-sm">
                {location.descriptionForm?.german || location.description || ""}
              </dd>
              <div className="flex flex-col mt-3 text-sm font-normal">
                <div className="flex items-center gap-1">
                  <dt className="flex items-center gap-2">
                    <LocalOffer className="rotate-90 w-[18px] h-[18px]" />
                    Miete:
                  </dt>
                  <dd className="grow">
                    {(location.priceLevel != null &&
                      location.priceLevel in locationPriceLevelValues &&
                      locationPriceLevelValues[
                        location.priceLevel as keyof typeof locationPriceLevelValues
                      ]) ||
                      "keine Angabe"}
                  </dd>
                </div>

                <div className="flex items-center gap-1">
                  <dt className="flex items-center gap-2">
                    <Person className="w-[18px] h-[18px]" />
                    max. Personen:
                  </dt>
                  <dd>
                    {(() => {
                      const maxCapacity = getMaxCapacity(location);
                      return maxCapacity ?? "keine Angabe";
                    })()}
                  </dd>
                </div>

                <div className="flex items-center">
                  <dt className="flex items-center gap-2">
                    <LocationIcon className="w-[18px] h-[18px]" />
                    <div className="sr-only">Ort:</div>
                  </dt>
                  <dd className="pl-2">{location.city}</dd>
                </div>
              </div>
            </dl>
          </div>
        </div>
        <div className="grid p-8 pt-0 gap-4">
          <button
            type="button"
            className="cursor-pointer w-full border-2 border-black rounded-md p-2 flex items-center gap-2"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(shareUrl);
                toast.success("Link kopiert");
              } catch (error) {
                console.error(error);
                toast.error("Fehler beim Kopieren des Links");
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width={24}
              height={24}
            >
              <path
                fill="#333"
                stroke="#060606"
                strokeWidth=".05"
                d="M7 6.87h3.333q.351 0 .588.242.238.243.238.595t-.238.587a.8.8 0 0 1-.588.235H7q-1.451 0-2.46 1.008-1.01 1.009-1.01 2.457t1.01 2.463T7 15.472h3.333q.351 0 .588.239.238.239.238.592a.8.8 0 0 1-.238.587.8.8 0 0 1-.588.235H7q-2.131 0-3.628-1.497Q1.875 14.132 1.876 12q0-2.13 1.496-3.63Q4.87 6.87 7 6.87Zm6.673 0H17q2.13 0 3.63 1.5t1.5 3.63q0 2.13-1.5 3.628-1.498 1.497-3.63 1.497h-3.327a.8.8 0 0 1-.594-.24.8.8 0 0 1-.238-.591q0-.352.238-.588a.8.8 0 0 1 .594-.234H17q1.451 0 2.46-1.01 1.012-1.007 1.012-2.456 0-1.448-1.011-2.463Q18.45 8.529 17 8.529h-3.327a.8.8 0 0 1-.594-.239.8.8 0 0 1-.238-.592.8.8 0 0 1 .238-.59.8.8 0 0 1 .594-.238ZM8.81 11.24h6.38a.74.74 0 0 1 .542.217.74.74 0 0 1 .22.548.73.73 0 0 1-.217.543.74.74 0 0 1-.545.213H8.81a.74.74 0 0 1-.542-.217.74.74 0 0 1-.22-.548.73.73 0 0 1 .762-.757Z"
              />
            </svg>
            <span className="text-center grow font-semibold">
              Link kopieren
            </span>
            <span className="w-[23px]"></span>
          </button>
          <EmailShareButton
            url={shareUrl}
            resetButtonStyle={false}
            className="cursor-pointer w-full border-2 border-black rounded-md p-2 flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 21 17"
              width={21}
              height={17}
              className="ml-[2px]"
            >
              <path
                fill="#333"
                stroke="#060606"
                d="M1.203 1.897v13.205h17.893V2.294l-.776.51-7.956 5.238q-.09.048-.158.075a.2.2 0 0 1-.057.006.2.2 0 0 1-.056-.006 2 2 0 0 1-.16-.075l-7.73-5.09v-.4l7.673 5.025.273.18.274-.179 8.347-5.45 1.01-.661q.024.116.025.242v12.893c0 .32-.113.594-.362.842-.248.248-.523.36-.847.36H1.703c-.322 0-.595-.112-.843-.36l-.087-.093A1.12 1.12 0 0 1 .5 14.6V1.71q.001-.133.026-.255zM1.703.5h16.893c.323 0 .597.113.846.362h.001q.164.164.25.347H.61q.087-.183.25-.348h.001C1.11.612 1.382.5 1.703.5Z"
              />
            </svg>
            <span className="text-center grow font-semibold">E-Mail</span>
            <span className="w-[23px]"></span>
          </EmailShareButton>
          <WhatsappShareButton
            url={shareUrl}
            resetButtonStyle={false}
            className="cursor-pointer w-full border-2 border-black rounded-md p-2 flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 23 23"
              width={23}
              height={23}
            >
              <path
                fill="#000"
                d="m0 23 1.617-5.906a11.4 11.4 0 0 1-1.521-5.699C.099 5.114 5.21 0 11.493 0a11.32 11.32 0 0 1 8.063 3.343 11.33 11.33 0 0 1 3.335 8.063c-.003 6.284-5.116 11.397-11.398 11.397a11.4 11.4 0 0 1-5.45-1.388zm6.322-3.648c1.606.953 3.14 1.524 5.167 1.525 5.221 0 9.474-4.249 9.477-9.473a9.47 9.47 0 0 0-9.469-9.48c-5.225 0-9.475 4.25-9.477 9.473 0 2.132.624 3.728 1.673 5.399l-.957 3.496zm10.913-5.237c-.071-.119-.261-.19-.547-.332-.284-.143-1.684-.832-1.946-.927-.26-.095-.45-.143-.641.143-.19.284-.736.926-.902 1.116s-.332.214-.617.071-1.203-.443-2.29-1.413c-.847-.755-1.419-1.688-1.585-1.973s-.017-.44.125-.581c.128-.128.285-.333.427-.5.145-.164.192-.283.288-.474.095-.19.048-.356-.024-.5-.072-.141-.641-1.543-.878-2.113-.232-.555-.467-.48-.641-.49l-.546-.009c-.19 0-.499.071-.76.357-.26.285-.996.973-.996 2.376 0 1.402 1.02 2.756 1.163 2.946.142.19 2.007 3.066 4.864 4.3a17 17 0 0 0 1.623.6c.683.216 1.304.185 1.794.113.548-.082 1.685-.69 1.923-1.355.238-.666.238-1.236.166-1.355"
              />
            </svg>
            <span className="text-center grow font-semibold">Whatsapp</span>
            <span className="w-[23px]"></span>
          </WhatsappShareButton>
          <FacebookShareButton
            url={shareUrl}
            resetButtonStyle={false}
            className="cursor-pointer w-full border-2 border-black rounded-md p-2 flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 23 23"
              width={23}
              height={23}
            >
              <g clipPath="url(#a)">
                <path
                  fill="#000"
                  d="M11.5 0C5.149 0 0 5.149 0 11.5c0 5.393 3.713 9.919 8.722 11.161v-7.647H6.351V11.5h2.371V9.986c0-3.914 1.771-5.729 5.614-5.729.729 0 1.986.143 2.5.286v3.185a15 15 0 0 0-1.328-.042c-1.886 0-2.614.714-2.614 2.571V11.5h3.756l-.645 3.514h-3.111v7.902C18.588 22.228 23 17.38 23 11.5 23 5.149 17.851 0 11.5 0"
                />
              </g>
              <defs>
                <clipPath id="a">
                  <path fill="#fff" d="M0 0h23v23H0z" />
                </clipPath>
              </defs>
            </svg>
            <span className="text-center grow font-semibold">Facebook</span>
            <span className="w-[23px]"></span>
          </FacebookShareButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
